// Shared text-formatting helper for turning internal snake_case keys (indicator
// names, evidence field names) into human-readable display text.
//
// Plain `.replace(/_/g,' ').split(' ').map(w => w[0].toUpperCase()+w.slice(1))` —
// used in a few places in this codebase — lower-cases every letter after the first
// in each word, which mangles acronyms and brand names ("Spf", "Hsts", "Dkim",
// "Mysql", "Github", "Powershell"). titleCaseWithAcronyms() applies that same
// word-by-word title-casing but looks each word up in ACRONYM_WORDS first, so known
// acronyms/brand names render correctly. Mirrors backend/services/text_format.py —
// keep the two lists in sync.
export const ACRONYM_WORDS: Record<string, string> = {
  // Domain & DNS
  spf: 'SPF', dkim: 'DKIM', dmarc: 'DMARC', dnssec: 'DNSSEC', dns: 'DNS',
  mx: 'MX', soa: 'SOA', bimi: 'BIMI', mta: 'MTA', sts: 'STS',
  ssl: 'SSL', caa: 'CAA', tld: 'TLD',
  // Network / web / TLS security
  rdp: 'RDP', ssh: 'SSH', vpn: 'VPN', ip: 'IP', cdn: 'CDN', waf: 'WAF',
  ldap: 'LDAP', mysql: 'MySQL', log4j: 'Log4j', tor: 'Tor',
  proxylogon: 'ProxyLogon', powershell: 'PowerShell',
  cms: 'CMS', openssl: 'OpenSSL', http: 'HTTP', https: 'HTTPS',
  csp: 'CSP', js: 'JS', cve: 'CVE', cves: 'CVEs', spring4shell: 'Spring4Shell',
  api: 'API', tls: 'TLS', hsts: 'HSTS', xss: 'XSS',
  // Cloud / infrastructure
  mfa: 'MFA', saas: 'SaaS', github: 'GitHub', kubernetes: 'Kubernetes',
  doh: 'DoH', ipv6: 'IPv6', asn: 'ASN', http2: 'HTTP2', http3: 'HTTP3',
  tcp: 'TCP', icmp: 'ICMP', edns: 'EDNS', ntp: 'NTP',
  // Patching cadence
  kev: 'KEV',
  // Funding & ownership
  ma: 'M&A',
  // Reputation & ESG
  esg: 'ESG',
  // Additional network service/protocol-detection indicators
  mongodb: 'MongoDB', mssql: 'MSSQL', postgresql: 'PostgreSQL', couchdb: 'CouchDB', openvpn: 'OpenVPN',
  pptp: 'PPTP', upnp: 'UPnP', rpc: 'RPC', snmp: 'SNMP', iot: 'IoT', soap: 'SOAP',
  ftp: 'FTP', socks: 'SOCKS', imap: 'IMAP', pop3: 'POP3', smtp: 'SMTP', vnc: 'VNC',
  nas: 'NAS', qnap: 'QNAP', ics: 'ICS', bacnet: 'BACnet', plc: 'PLC', dnp3: 'DNP3',
  sslv2: 'SSLv2', sslv3: 'SSLv3', rc4: 'RC4', '3des': '3DES', airport: 'AirPort',
  db: 'DB', sql: 'SQL',
  // Whole-token overrides for keys containing "/" or "."
  'netbios/smb': 'NetBIOS/SMB', 'ssl/tls': 'SSL/TLS', 'tlsv1.0': 'TLSv1.0',
};

/** "spf_effectiveness" -> "SPF Effectiveness" (not "Spf Effectiveness"). */
export function titleCaseWithAcronyms(key: string): string {
  return key
    .replace(/-/g, '_')
    .split('_')
    .filter(Boolean)
    .map((word) => ACRONYM_WORDS[word.toLowerCase()] || word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
